import {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import CoverLayout from "layouts/authentication/components/CoverLayout";
import {setAuthentication, setLoggedInUserDetail, useRayzayUIController} from "../../../context";
import SoftAlert from "../../../components/Alert";
import {getAllUserByUserName} from "../../../api/user/userApiClient";
import axiosApiClient from "../../../api/axios";

function ChangePassword() {
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState(null);
    const [showAlert, setShowAlert] = useState(false);
    const [loading, setLoading] = useState(false);
    const [controller, dispatch] = useRayzayUIController();
    const {isAuthenticated, loggedInUserDetail} = controller;
    const navigate = useNavigate();

    useEffect(() => {
        if (!isAuthenticated || !loggedInUserDetail) {
            navigate("/authentication/sign-in");
        }
    }, [isAuthenticated, loggedInUserDetail, navigate]);

    const handleChangePassword = async () => {
        if (newPassword !== confirmPassword) {
            setError("New password and confirmation do not match");
            setShowAlert(true);
            return;
        }

        if (newPassword.length < 8) {
            setError("New password must be at least 8 characters long");
            setShowAlert(true);
            return;
        }

        setLoading(true);
        try {
            const response = await axiosApiClient.post('/api/auth/change-password', {
                username: loggedInUserDetail.username,
                currentPassword: currentPassword,
                newPassword: newPassword,
                confirmPassword: confirmPassword
            });

            if (response.status === 200) {
                // Password changed successfully, redirect to dashboard
                navigate("/dashboard");
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to change password');
            setShowAlert(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <CoverLayout
            title="Change Password"
            description="Please change your password to continue"
        >
            {error && (
                <SoftAlert
                    color="error"
                    dismissible={false}
                    timeout={10}
                    mount={showAlert}
                    onClose={() => {
                        setError(null);
                        setShowAlert(false);
                    }}
                >
                    {error}
                </SoftAlert>
            )}
            <SoftBox component="form" role="form">
                <SoftBox mb={2}>
                    <SoftBox mb={1} ml={0.5}>
                        <SoftTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold"
                        >
                            Current Password
                        </SoftTypography>
                    </SoftBox>
                    <SoftInput
                        type="password"
                        placeholder="Current Password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                    />
                </SoftBox>
                <SoftBox mb={2}>
                    <SoftBox mb={1} ml={0.5}>
                        <SoftTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold"
                        >
                            New Password
                        </SoftTypography>
                    </SoftBox>
                    <SoftInput
                        type="password"
                        placeholder="New Password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                    />
                </SoftBox>
                <SoftBox mb={2}>
                    <SoftBox mb={1} ml={0.5}>
                        <SoftTypography
                            component="label"
                            variant="caption"
                            fontWeight="bold"
                        >
                            Confirm New Password
                        </SoftTypography>
                    </SoftBox>
                    <SoftInput
                        type="password"
                        placeholder="Confirm New Password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                </SoftBox>
                <SoftBox mt={4} mb={1}>
                    <SoftButton
                        variant="gradient"
                        color="primary"
                        fullWidth
                        onClick={handleChangePassword}
                        disabled={loading}
                    >
                        {loading ? "Changing Password..." : "Change Password"}
                    </SoftButton>
                </SoftBox>
            </SoftBox>
        </CoverLayout>
    );
}

export default ChangePassword;
